import { PulseLoader } from '@sparemin/blockhead';
import React, { useCallback, useMemo, useState } from 'react';
import DestinationPlatforms, {
  checkIfDestinationPresetExists,
  DestinationPlatform,
  DestinationPlatformsVideoTypes,
  getPlatformConfig,
  VideoTypeConfig,
} from 'blocks/DestinationPlatforms';
import { AspectRatioName } from 'types';
import { VideoWizardObjective } from '../types';
import { VIDEO_WIZARD_VIDEO_TYPES_BLACKLIST } from './constants';
import { block, getDestinationVideoTypes, getDisabledPlatforms } from './utils';

export interface VideoWizardDestinationStepProps {
  objective: VideoWizardObjective;
  isLoading?: boolean;
  requiredAspectRatio?: AspectRatioName;
  onDestinationChosen?: (
    platform: DestinationPlatform,
    videoType?: VideoTypeConfig,
  ) => void;
}

const VideoWizardDestinationStep: React.FC<VideoWizardDestinationStepProps> = ({
  objective,
  isLoading,
  requiredAspectRatio,
  onDestinationChosen,
}) => {
  const [platform, setPlatform] = useState<DestinationPlatform | undefined>(
    undefined,
  );

  const platformConfig = getPlatformConfig(
    platform,
    'videoTranscript',
    VIDEO_WIZARD_VIDEO_TYPES_BLACKLIST,
  );

  const disabledPlatforms = useMemo(
    () => getDisabledPlatforms(requiredAspectRatio),
    [requiredAspectRatio],
  );

  const videoTypes = useMemo(
    () =>
      getDestinationVideoTypes(
        platformConfig,
        objective,
        requiredAspectRatio,
        platform,
      ),
    [objective, platform, platformConfig, requiredAspectRatio],
  );

  const handlePlatformSelection = useCallback(
    (newPlatform: DestinationPlatform): void => {
      setPlatform(newPlatform);

      if (!checkIfDestinationPresetExists(newPlatform)) {
        onDestinationChosen(newPlatform, {
          id: 'post',
        });
      }
    },
    [onDestinationChosen],
  );

  const handleVideoTypeSelections = useCallback(
    (videoType: VideoTypeConfig): void => {
      onDestinationChosen(platform, videoType);
    },
    [onDestinationChosen, platform],
  );

  if (isLoading) {
    return (
      <div className={block('loading')}>
        <PulseLoader />
      </div>
    );
  }

  return !platform ||
    (platform && !checkIfDestinationPresetExists(platform)) ? (
    <DestinationPlatforms
      contentsClassName={block('destination-options')}
      disabledPlatforms={disabledPlatforms}
      onPlatformSelection={handlePlatformSelection}
    />
  ) : (
    <div className={block('video-types-container')}>
      <DestinationPlatformsVideoTypes
        icon={platformConfig?.icon}
        videoTypes={videoTypes}
        onClick={handleVideoTypeSelections}
        onBackClick={() => setPlatform(undefined)}
      />
    </div>
  );
};

export default VideoWizardDestinationStep;
